import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import useAppQuery from 'hooks/useAppQuery';
import { config } from 'variables/config';
import { useTranslation } from 'react-i18next';
import DisplayOnlyField from './fields/DisplayOnlyField';
import HistoryTooltip from './fields/HistoryTooltip';

export default function MuiAsyncAutocomplete({
  error,
  fullWidth,
  fetch,
  value,
  label,
  handleChange,
  format,
  defaultFormat,
  wrapperClasses,
  multiple,
  disabled,
  displayOnly,
  withHistory,
  history = [],
  minChars = 3,
  debounceMs = 400,
}) {
  const { t } = useTranslation('zones_list');

  if (defaultFormat) format = { value: 'id', label: 'name' };

  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(null);
  const debounceRef = React.useRef(null);

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const {
    isLoading,
    isFetching,
    data: options,
  } = useAppQuery(searchTerm ? `${config.app.api_url}/${fetch}/${searchTerm}` : null, {
    staleTime: 60_000,
  });

  const loading = isLoading || isFetching;

  const handleInputChange = (event, inputValue, reason) => {
    // 'reset' fires when MUI clears the input after selection — don't re-trigger a search
    if (reason === 'reset') return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!inputValue || inputValue.length < minChars) {
      setSearchTerm(null);
      return;
    }

    debounceRef.current = setTimeout(() => {
      setSearchTerm(inputValue);
    }, debounceMs);
  };

  const resolveLabel = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return val?.label ?? val?.[format?.label] ?? val?.value ?? '';
    return val;
  };

  const displayText = multiple
    ? (value || [])
        .map((v) => resolveLabel(v))
        .filter(Boolean)
        .join(', ')
    : resolveLabel(value);

  if (displayOnly) {
    return (
      <div className="flex items-center gap-2 w-full">
        <div className={`${wrapperClasses} flex-1`}>
          <DisplayOnlyField label={label} value={displayText} />
        </div>
        <HistoryTooltip show={withHistory} history={history} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-1">
      <Autocomplete
        multiple={multiple}
        disabled={disabled}
        className={wrapperClasses}
        fullWidth
        sx={fullWidth ? { flex: 1 } : { width: 300 }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        getOptionLabel={(option) => option?.label || ''}
        filterOptions={(x) => x}
        options={
          options?.data
            ? options.data.map((d) => ({ label: d[format?.label], value: d[format?.value] }))
            : []
        }
        loading={loading}
        onChange={handleChange}
        value={value ?? (multiple ? [] : null)}
        noOptionsText={searchTerm ? t('no_result') : t('type_to_search', { count: minChars })}
        onInputChange={handleInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      <HistoryTooltip show={withHistory} history={history} />
    </div>
  );
}
